import { useState } from 'react';
import { Form, Row, Col, InputGroup, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEuroSign, faReceipt, faExchangeAlt, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../../utils/formatCurrency';

export default function ReverseCalculator({ settings }) {
  const { t } = useTranslation();

  const [partCostInput, setPartCostInput] = useState('');
  const [finalTotalInput, setFinalTotalInput] = useState('');
  const [useCard, setUseCard] = useState(settings.defaultCardPayment ?? false);

  const partCostValue = parseFloat(partCostInput) || 0;
  const finalTotalValue = parseFloat(finalTotalInput) || 0;

  let paymentFee = 0;
  if (useCard && finalTotalValue > 0) {
    paymentFee = finalTotalValue * ((settings.cardFeePercentage || 0) / 100) + (settings.cardFixedFee || 0);
  }

  const grossReceived = finalTotalValue - paymentFee;
  const subTotal = grossReceived / (1 + (settings.ivaPercentage || 0) / 100);
  const ivaAmount = grossReceived - subTotal;
  const netProfit = subTotal - partCostValue;

  return (
    <Row className="g-4">
      <Col lg={7}>
        <div className="repaircalc-section">
          <h5 className="mb-2">
            <FontAwesomeIcon icon={faExchangeAlt} className="text-primary me-2" />
            {t('repaircalc.reverse.title')}
          </h5>
          <p className="text-muted mb-4">{t('repaircalc.reverse.description')}</p>

          <Form.Group className="mb-4">
            <Form.Label>{t('repaircalc.reverse.totalPaid')}</Form.Label>
            <InputGroup>
              <InputGroup.Text><FontAwesomeIcon icon={faEuroSign} /></InputGroup.Text>
              <Form.Control
                type="number"
                value={finalTotalInput}
                onChange={(e) => setFinalTotalInput(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0.00"
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('repaircalc.reverse.partCost')}</Form.Label>
            <InputGroup>
              <InputGroup.Text><FontAwesomeIcon icon={faEuroSign} /></InputGroup.Text>
              <Form.Control
                type="number"
                value={partCostInput}
                onChange={(e) => setPartCostInput(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0.00"
              />
            </InputGroup>
          </Form.Group>

          <Form.Group>
            <Form.Label>{t('repaircalc.reverse.cardQuestion')}</Form.Label>
            <Card
              className={`payment-card ${useCard ? 'border-primary' : ''}`}
              onClick={() => setUseCard(!useCard)}
            >
              <Card.Body className="py-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCreditCard} className={`me-3 fs-5 ${useCard ? 'text-primary' : 'text-muted'}`} />
                  <div>
                    <div className="fw-medium">{t('repaircalc.reverse.electronicPayment')}</div>
                    <div className="text-muted small">
                      {t('repaircalc.reverse.subtractFees')}: {settings.cardFeePercentage}% + {settings.cardFixedFee}€
                    </div>
                  </div>
                </div>
                <div className={`round-selector ${useCard ? 'active' : ''}`}>
                  {useCard && <span className="round-dot" />}
                </div>
              </Card.Body>
            </Card>
          </Form.Group>
        </div>
      </Col>

      <Col lg={5}>
        <div className="receipt-card">
          <div className="d-flex align-items-center mb-4">
            <FontAwesomeIcon icon={faReceipt} className="me-2" />
            <span className="receipt-heading">{t('repaircalc.reverse.analysisTitle')}</span>
          </div>

          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.reverse.totalCollected')}</span>
            <span className="receipt-value">{formatCurrency(finalTotalValue, 'EUR')}</span>
          </div>
          {useCard && (
            <div className="receipt-row">
              <span className="receipt-label">{t('repaircalc.reverse.posFees')}</span>
              <span className="text-danger">-{formatCurrency(paymentFee, 'EUR')}</span>
            </div>
          )}
          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.reverse.vatBreakdown')} ({settings.ivaPercentage || 0}%)</span>
            <span className="text-danger">-{formatCurrency(ivaAmount, 'EUR')}</span>
          </div>

          <hr className="receipt-divider" />

          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.reverse.netRevenue')}</span>
            <span className="receipt-value">{formatCurrency(subTotal, 'EUR')}</span>
          </div>
          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.calculator.partCostLabel')}</span>
            <span className="text-warning">-{formatCurrency(partCostValue, 'EUR')}</span>
          </div>

          <hr className="receipt-divider" />

          <div className="receipt-total">
            <span className="receipt-total-label">{t('repaircalc.reverse.realProfit')}</span>
            <span className={`receipt-total-value ${netProfit >= 0 ? 'text-profit' : 'text-danger'}`}>
              {formatCurrency(netProfit, 'EUR')}
            </span>
          </div>
        </div>
      </Col>
    </Row>
  );
}
